import { GetOrganizationSetupStatusAction } from "../store/action";
import { isOrgOwnerCapableRole } from "../constants/navigation";

/**
 * Resolves whether the caller's organization has completed setup.
 *
 * Only a CompanyAdmin can ever come back incomplete — every other role always
 * resolves `true`, since finishing setup is an org owner's job, not theirs, so
 * there's nothing to fetch. Platform staff (SystemOwner, Admin) have no
 * organization at all and no tab that reads one.
 * Also resolves `true` (fail open) on any fetch error, so a network hiccup
 * never blocks an otherwise-working dashboard or its routes.
 *
 * Shared by `Dashboard.jsx` (drives the sidebar) and `App.jsx`'s
 * `DashboardRoute` (drives direct-URL access) so the two can't drift apart.
 */
export const resolveOrganizationSetupComplete = async (role) => {
  if (!isOrgOwnerCapableRole(role)) return true;

  try {
    const status = await GetOrganizationSetupStatusAction();
    return status?.isSetupComplete ?? true;
  } catch (error) {
    console.error("Error fetching organization setup status:", error);
    return true;
  }
};

/**
 * A one-way channel from the wizard to `Dashboard.jsx`'s sidebar.
 *
 * `Dashboard.jsx` reads `isSetupComplete` once on mount and passes it down to
 * `DashboardMenu` to gate the 7 setup-locked nav items. The wizard runs on
 * the same `/dashboard/organization` route Dashboard is already mounted on,
 * so finishing it doesn't remount Dashboard or re-trigger that fetch — the
 * sidebar items stayed locked until the next full page load even though
 * setup had just completed. Wizard and sidebar are otherwise unrelated
 * components with no shared state, so they talk through a window event
 * rather than threading a callback prop through the whole routes array (see
 * `components/ui/contactIntent.js` for the same pattern).
 */
export const ORGANIZATION_SETUP_COMPLETE_EVENT =
  "easyplanner:organization-setup-complete";

/** Tell any mounted sidebar to re-check setup status right now. */
export const notifyOrganizationSetupComplete = () => {
  window.dispatchEvent(new Event(ORGANIZATION_SETUP_COMPLETE_EVENT));
};
